/**
 * SHIP MODE — the frugal 2D renderer, wired to declared geometry.
 *
 * Both surfaces, per the founder: mobile is an ADDITIONAL surface, not the primary one. A laptop
 * gets profile and section side by side, because reading them together is what teaches; a phone gets
 * one at a time with a switch. `pickLayout()` decides on measured WIDTH, never a user-agent string.
 *
 * WHAT IS DRAWN IS WHAT IS DECLARED. The envelope and the stations follow by arithmetic from LBP, B
 * and D. Floors, girders and the bilge radius are NOT drawn and the surface SAYS so, because a
 * candidate cannot tell an invented member from a declared one by looking.
 */
import { useEffect, useRef, useState } from 'react';
import { mountView, VIEWS, pickLayout } from '../../../packages/ankr-glass/src/renderer2d.mjs';
import { Value, useWidgetData } from '../../../packages/ankr-glass/src/react.jsx';
import { plan } from '../../../packages/ankr-vahana/src/plan.mjs';
import { sectionFromDimensions, profileFromDimensions } from './geometry.mjs';
/* Fleet, HullGirder and Sheets came here when Learn and Practice were rebuilt natively. They were
   good surfaces about GEOMETRY sitting under a teaching tab, and rebuilding those two modes left all
   three rendered by nothing — three working capabilities unreachable, which is the quiet kind of
   regression: no error, no failing build, just a candidate who can no longer compare the fleet. */
import { API, Fleet, HullGirder, Sheets } from './surfaces.jsx';

const FLEET = { slug: 'fleet-comparison', layout: 'auto-table',
  wire: { kind: 'rest', route: '/api/fleet', method: 'GET', field: 'fleet' } };
const MEMBERS = { slug: 'member-coordinates', layout: 'auto-table',
  wire: { kind: 'rest', route: '/api/members', method: 'GET', field: 'members' } };
// The CATALOGUE. Each hull declares ~30 views; hardcoding buttons for them would be a second list
// beside the one the ship already carries, and a second list is the drift that put the forepeak in
// 2D and not 3D this morning. The ship is asked what it has.
const CATALOGUE = { slug: 'view-catalogue', layout: 'auto-table',
  wire: { kind: 'rest', route: '/api/members', method: 'GET', field: 'views' } };

/**
 * The endpoint returns segments already projected into the cut plane as {u,v}. The renderer works in
 * named axes, so u/v are mapped onto the view's own pair — no coordinate maths happens in the
 * browser, which is the point: one engine cuts the geometry and both renderers consume it.
 */
function membersToPoints(apiMembers, view) {
  const [ax, ay] = view.axes;
  const out = [];
  for (const m of apiMembers || [])
    for (let i = 0; i < (m.segments || []).length; i++) {
      const [a, b] = m.segments[i];
      out.push({ id: `${m.id}:${i}`, label: m.label, layer: m.layer,
                 points: [{ [ax]: a.u, [ay]: a.v }, { [ax]: b.u, [ay]: b.v }] });
    }
  return out;
}

/** One canvas, one view. The renderer owns DPR and resize; React only supplies the data. */
function ViewPane({ members, view, labels, highlight }) {
  const ref = useRef(null), handle = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    handle.current = mountView(ref.current, { members, view, labels, highlight, theme: {
      ink: getComputedStyle(document.documentElement).getPropertyValue('--g-ink').trim() || '#e8eef4',
      dim: getComputedStyle(document.documentElement).getPropertyValue('--g-ink-3').trim() || '#5d7888',
      accent: getComputedStyle(document.documentElement).getPropertyValue('--g-accent').trim() || '#38b6e8',
      bg: getComputedStyle(document.documentElement).getPropertyValue('--g-depth-1').trim() || '#070b11',
    }});
    return () => handle.current?.destroy();
  }, []);                                    // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { handle.current?.update({ members, view, labels, highlight }); },
            [members, view, labels, highlight]);
  return <canvas ref={ref} className="ship-canvas" aria-label={`${view.label} view`} />;
}

/** Measured, not guessed — a phone in landscape on a stand earns the laptop composition. */
function useLayout() {
  const [w, setW] = useState(() => (typeof window === 'undefined' ? 1024 : window.innerWidth));
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    addEventListener('resize', on); return () => removeEventListener('resize', on);
  }, []);
  return pickLayout(w);
}

export default function Ship() {
  const st = useWidgetData(FLEET, {}, { baseUrl: API, plan });
  const ships = st.data?.ships || [];
  const [key, setKey] = useState('abhyas');
  const ship = ships.find((s) => s.key === key) || ships[0];
  const layout = useLayout();
  const [one, setOne] = useState(VIEWS.SECTION.id);

  // Declared structure when the engine has it; the dimension-derived envelope when it does not.
  const [station, setStation] = useState(0);
  // A DECLARED VIEW (forepeak, anchor arrangement) is a transverse section that needs no station —
  // and needs no class-society extents either, which is why it is available now while the 3D peak
  // is not. `null` means "cut the hull at a station" instead.
  const [namedView, setNamedView] = useState(null);
  const cat = useWidgetData(CATALOGUE, { ship: key, view: '*' }, { baseUrl: API, plan });
  const allViews = cat.data?.views || [];

  const cut = useWidgetData(MEMBERS,
    namedView ? { ship: key, view: namedView } : { ship: key, plane: 'transverse', at: station },
    { baseUrl: API, plan });
  const cutMembers = cut.data?.members || [];

  const sec = sectionFromDimensions(ship);
  const pro = profileFromDimensions(ship);
  const real = cutMembers.length ? membersToPoints(cutMembers, VIEWS.SECTION) : null;
  const forView = (v) => (v.id === 'section' ? (real || sec.members) : pro.members);
  const shown = layout.switchable
    ? [Object.values(VIEWS).find((v) => v.id === one) || VIEWS.SECTION]
    : layout.views;

  return (
    <div className="shell-col g-rise">
      <div className="g-glass shell-panel">
        <div className="g-label">Structure · orthographic</div>
        <h2 className="g-h2" style={{ margin: 'var(--g-2) 0 var(--g-3)' }}>
          {ship?.ship || 'Select a vessel'}
        </h2>
        <p className="g-body">
          This is the form the examination is set in — profile and transverse section, drawn to scale
          from the vessel&rsquo;s declared dimensions. It needs no 3D support and runs on any device.
        </p>

        <div className="shell-picker" style={{ marginTop: 'var(--g-3)' }}>
          {ships.map((s) => (
            <button key={s.key} onClick={() => setKey(s.key)}
              className={`g-btn ${key === s.key ? 'g-btn-primary' : ''}`}>
              {s.ship?.replace(/^M\.[VT]\.\s*/, '') || s.key}
            </button>
          ))}
        </div>

        {/* Only a narrow surface needs this — on a laptop both views are already on screen. */}
        {layout.switchable && (
          <div className="shell-picker" style={{ marginTop: 'var(--g-2)' }}>
            {Object.values(VIEWS).map((v) => (
              <button key={v.id} onClick={() => setOne(v.id)}
                className={`g-btn ${one === v.id ? 'g-btn-primary' : ''}`}>{v.label}</button>
            ))}
          </div>
        )}

        <div className={`ship-views cols-${shown.length}`}>
          {shown.map((v) => (
            <ViewPane key={v.id} view={v} members={forView(v)} labels={layout.labels} />
          ))}
        </div>

        {/* The limit is stated ON the surface, not in a comment nobody reads. */}
        <div className="shell-picker" style={{ marginTop: 'var(--g-2)' }}>
          {/* THE ORIGIN IS MIDSHIPS, not the aft perpendicular. Stations run -L/2 aft to +L/2
              forward, so 0 is ⊗ and ±88 are the perpendiculars on a 176 m hull. Shipped once with
              an AP-origin picker (22/44/66/88), which put three of four buttons forward of midships
              and one on the stem — where only the shell exists, making a complete model look
              nearly empty. A station number means nothing without its datum. */}
          {/* -70 was labelled "Aft", which was true and useless. Since the engine room was promoted
              to solids it cuts 9 engine-room members there on six of seven hulls (5 on samarth), so
              the station now says what a candidate would actually find. The Peak station needed
              adding because the picker could not reach it; this one only needed telling the truth. */}
          {[{ at: -70, l: 'Engine room' }, { at: -40, l: 'Hold aft' }, { at: 0, l: '⊗ Midships' },
            { at: 40, l: 'Hold fwd' }, { at: 70, l: 'Fwd' },
            // 82 m forward of midships is INSIDE the declared peak on all seven hulls (verified:
            // 10 forepeak members on each). The picker previously stopped at 70, which is aft of
            // every collision bulkhead in the fleet — so the promoted peak existed in solidsOf and
            // was invisible on the surface. A station list that cannot reach a region is the same
            // as that region not being modelled, from the candidate's side of the screen.
            { at: 82, l: 'Peak' }].map((x) => (
            <button key={x.at} onClick={() => { setNamedView(null); setStation(x.at); }}
              className={`g-btn ${!namedView && station === x.at ? 'g-btn-primary' : ''}`}>{x.l}</button>
          ))}
          {/* The sternframe is a NAMED VIEW, not a station, and that is the honest instrument for
              it. A transverse cut at -84 finds 2 of its members, because the keel, sole piece,
              propeller post, rudder post and arch are a CENTRELINE assembly — a section across
              the ship barely touches them. Its declared profile carries all 7 drawable members
              at once. A station would have produced a nearly empty drawing and called it the
              sternframe. */}
          {[{ v: 'forepeak', l: 'Forepeak' }, { v: 'anchor_arrangement', l: 'Anchor & cable' },
            { v: 'sternframe', l: 'Sternframe' }].map((x) => (
            <button key={x.v} onClick={() => setNamedView(x.v)}
              className={`g-btn ${namedView === x.v ? 'g-btn-primary' : ''}`}>{x.l}</button>
          ))}

          {/* Every other declared view, from the ship's own catalogue. A select rather than thirty
              more buttons: thirty would bury the three a candidate reaches for most, and would not
              fit a phone. Each option shows its DRAWABLE count so nobody picks a view expecting
              more than it renders. */}
          {allViews.length > 0 && (
            <select className="g-input shell-viewsel" value={namedView || ''}
              onChange={(e) => setNamedView(e.target.value || null)}
              aria-label="All declared views">
              <option value="">All views ({allViews.length})…</option>
              {allViews.map((v) => (
                <option key={v.name} value={v.name}>
                  {v.label} — {v.drawable}{v.drawable !== v.declared ? ` of ${v.declared}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="ship-limits">
          <div className="g-label">
            {real ? namedView
              ? `${cutMembers.length} declared members — ${namedView.replace(/_/g, ' ')}`
              : `${cutMembers.length} declared members at station ${station > 0 ? '+' : ''}${station} m from midships` : 'What is not drawn'}
          </div>
          {/* When the engine returns nothing at a station that is a FACT about where the solids
              are, not a failure — so its own note is shown rather than an empty drawing. */}
          {cut.envelope ? <div className="ship-limit"><Value of={cut.envelope} /></div>
            : cut.data?.note ? <div className="ship-limit g-body g-dim">{cut.data.note}</div>
            : null}
          {!real && sec.note ? <div className="ship-limit"><Value of={sec.note} /></div> : null}
          {pro.note ? <div className="ship-limit"><Value of={pro.note} /></div> : null}
        </div>
      </div>

      {/* THE FLEET AND HER SCANTLINGS — geometry, under the geometry tab.
          The picker is shared: choosing a hull above changes these too, because a candidate
          comparing sections and then reading their section elements is following one thought. */}
      <Fleet picked={key} onPick={setKey} />
      <HullGirder ship={key} />
      <Sheets ship={key} />
    </div>
  );
}
